import { computed, ref } from 'vue'
import { getOffersAction } from '@/api/functions/offers/get-offers.action'
import type { Offer, OfferFilters } from '@/api/typings/offer.typing'
import { useQueryApi } from './useApi'

export function useOffers(initialFilters: OfferFilters = {}) {
  const currentPage = ref(initialFilters.page || 1)
  const activeFilters = ref<OfferFilters>({ ...initialFilters })

  const query = useQueryApi({
    queryKey: computed(() => ['offers', { ...activeFilters.value, page: currentPage.value }]),
    queryFn: () =>
      getOffersAction({
        ...activeFilters.value,
        page: currentPage.value,
      }),
    // keepPreviousData: true,

    staleTime: 1000 * 60 * 3, // cache data for 3 minutes
  })

  const offers = computed<Offer[]>(() => query.data.value?.data || [])
  const pagination = computed(() => query.data.value?.pagination)
  const message = computed(() => query.data.value?.meta?.message)

  const nextPage = () => {
    if (pagination.value?.hasNextPage) currentPage.value++
  }

  const prevPage = () => {
    if (currentPage.value > 1) currentPage.value--
  }

  const goToPage = (page: number) => {
    currentPage.value = page
  }

  const updateFilters = (newFilters: OfferFilters) => {
    const cleanFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    )
    activeFilters.value = { ...cleanFilters }
    currentPage.value = 1
  }

  const refetchOffers = async () => {
    await query.refetch()
  }

  return {
    // state
    offers,
    pagination,
    message,
    currentPage,
    activeFilters,

    // methods
    nextPage,
    prevPage,
    goToPage,
    updateFilters,
    refetchOffers,

    // vue-query fields
    ...query,
  }
}
